import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const SavingsGoalCalculator = () => {
  // State variables
  const [initialDeposit, setInitialDeposit] = useState("");
  const [monthlyDeposit, setMonthlyDeposit] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [years, setYears] = useState("");
  //   const [goalAmount, setGoalAmount] = useState('');
  const [futureSavings, setFutureSavings] = useState(0);

  // Recalculate future savings whenever any of the inputs change
  useEffect(() => {
    if (initialDeposit && monthlyDeposit && interestRate && years) {
      // Formula for compound savings goal
      const n = years * 12; // Convert years to months
      const r = interestRate / 100 / 12; // Monthly interest rate
      const futureSavings =
        initialDeposit * Math.pow(1 + r, n) +
        monthlyDeposit * ((Math.pow(1 + r, n) - 1) / r);
      setFutureSavings(futureSavings);
    } else {
      setFutureSavings(0); // In case of incomplete input
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [initialDeposit, monthlyDeposit, interestRate, years]);

  // Event handlers
  const handleInitialDepositChange = (e) =>
    setInitialDeposit(parseFloat(e.target.value) || 0);
  const handleMonthlyDepositChange = (e) =>
    setMonthlyDeposit(parseFloat(e.target.value) || 0);
  const handleInterestRateChange = (e) =>
    setInterestRate(parseFloat(e.target.value) || 0);
  const handleYearsChange = (e) => setYears(parseFloat(e.target.value) || 0);
  //   const handleGoalAmountChange = (e) =>
  //     setGoalAmount(parseFloat(e.target.value) || 0);

  return (
    <Layout title="Savings Goal Calculator" path="savings-goal">
      <section className="section-padding" id="savings-goal-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Savings Goal Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Savings Goal Calculator</h3>
                <div className="container">
                  {/* Input Fields for Savings Goal Calculation */}
                  <div className="row gy-3">
                    {/* Initial Deposit */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="initialDeposit" className="form-label">
                        Initial Deposit (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Initial Deposit:</strong> The starting amount of money you want to invest or save."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="initialDeposit"
                        placeholder="Enter initial deposit"
                        value={initialDeposit}
                        onChange={handleInitialDepositChange}
                      />
                    </div>

                    {/* Monthly Deposit */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="monthlyDeposit" className="form-label">
                        Monthly Deposit (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Monthly Deposit:</strong> The amount you plan to add to your savings every month."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyDeposit"
                        placeholder="Enter monthly deposit"
                        value={monthlyDeposit}
                        onChange={handleMonthlyDepositChange}
                      />
                    </div>

                    {/* Interest Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="interestRate" className="form-label">
                        Interest Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Interest Rate:</strong> The annual interest rate (in percentage) that you expect to earn on your savings."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="interestRate"
                        placeholder="Enter interest rate"
                        value={interestRate}
                        onChange={handleInterestRateChange}
                      />
                    </div>

                    {/* Years */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="years" className="form-label">
                        Years
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Years:</strong> The total duration (in years) over which you plan to save money."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="years"
                        placeholder="Enter number of years"
                        value={years}
                        onChange={handleYearsChange}
                      />
                    </div>

                    {/* Goal Amount
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="goalAmount" className="form-label">
                        Savings Goal (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="goalAmount"
                        placeholder="Enter your savings goal"
                        value={goalAmount}
                        onChange={handleGoalAmountChange}
                      />
                    </div> */}
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Future Savings:{" "}
                        <span className="fw-bold ms-3">
                          ₹
                          {futureSavings.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Savings Goal Calculator
                </h3>

                {/* What is a Savings Goal Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Savings Goal Calculator?
                </h4>
                <p>
                  A **Savings Goal Calculator** helps estimate how much you need
                  to save and how long it will take to reach your financial
                  goals based on regular deposits, interest rates, and the power
                  of compounding. This tool is useful for both short-term and
                  long-term savings planning.
                </p>

                {/* Why Use a Savings Goal Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Savings Goal Calculator?
                </h4>
                <p>
                  There are several benefits of using a savings goal calculator:
                </p>
                <ul>
                  <li>
                    <strong>
                      Helps Individuals Plan and Track Savings Goals:
                    </strong>
                    Allows users to visualize their progress and determine if
                    they are on track to reach their target.
                  </li>
                  <li>
                    <strong>
                      Useful for Retirement or Major Purchases Planning:
                    </strong>
                    Aids in planning for significant life events such as buying
                    a home, funding education, or retirement.
                  </li>
                  <li>
                    <strong>Encourages Consistent Savings Behavior:</strong>
                    Provides a structured approach to saving, helping users stay
                    disciplined in their savings habits.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who is this Calculator Suitable For?
                </h4>
                <p>This calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Personal Finance Enthusiasts</strong>
                  </li>
                  <li>
                    <strong>Financial Advisory Services</strong>
                  </li>
                  <li>
                    <strong>Banks and Financial Institutions</strong>
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is the purpose of a savings goal calculator?
                </h4>
                <p>
                  The purpose is to help individuals set realistic savings goals
                  based on their income, expenses, and target amount. It takes
                  into account the power of compound interest to show how
                  regular deposits can grow over time.
                </p>

                <h4 className="mt-4 mb-3">
                  How does compound interest affect savings?
                </h4>
                <p>
                  Compound interest helps savings grow faster because it applies
                  interest on both the initial deposit and the accumulated
                  interest, making your savings grow exponentially over time.
                </p>

                <h4 className="mt-4 mb-3">
                  What are the key factors to consider when planning for savings
                  goals?
                </h4>
                <p>
                  Key factors include the amount of initial deposit, monthly
                  deposits, interest rate, and the length of time for the
                  investment or savings goal. All these elements affect how
                  quickly you will reach your target.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SavingsGoalCalculator;
